/**
 * `SubTabs`/`SubPanel` — the UC1/UC2 result panes' second-level tab strip (design doc
 * `2026-09-28-intel-subtabs-design.md` §1): `#uc1Tabs`/`#uc2Tabs`, buttons
 * `#<prefix>Tab-<name>`, panels `#<prefix>Panel-<name>`. Generic over `prefix` and the tab
 * name list so UC1 (`overview`/`indicators`/`why`/`method`) and UC2
 * (`overview`/`trends`/`practices`/`similar`/`method`) share one component.
 *
 * Same roving-tabindex `tablist` as `Tabs.tsx` (`#intelTabs`): arrow keys move focus *and*
 * selection together, Home/End jump to the ends, a native `<button>` gives Enter/Space for
 * free. Reads/writes `intel.subTab` directly (a single string shared by both use cases'
 * strips) rather than a tab-specific slice — `#intelSelect` and `#intelReset` already reset it
 * to `'overview'` for free by spreading `INITIAL_INTEL_STATE`, and a stored value that is not
 * one of *this* strip's own tabs (e.g. left over from the other use case) falls back to the
 * first tab rather than rendering nothing selected.
 */
import { useCallback, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppStore } from '../../lib/state';

export function SubTabs({ prefix, tabs }: { prefix: 'uc1' | 'uc2'; tabs: readonly string[] }) {
  const { t } = useTranslation();
  const stored = useAppStore((s) => s.intel.subTab);
  const setIntelSubTab = useAppStore((s) => s.setIntelSubTab);
  const listRef = useRef<HTMLDivElement>(null);

  const active = tabs.includes(stored) ? stored : tabs[0]!;

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const i = tabs.indexOf(active);
      let next = i;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % tabs.length;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp')
        next = (i - 1 + tabs.length) % tabs.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = tabs.length - 1;
      else return;
      e.preventDefault();
      const nextTab = tabs[next]!;
      setIntelSubTab(nextTab);
      listRef.current?.querySelector<HTMLButtonElement>(`#${prefix}Tab-${nextTab}`)?.focus();
    },
    [tabs, active, prefix, setIntelSubTab],
  );

  return (
    <div
      id={`${prefix}Tabs`}
      className="intel-subtabs"
      role="tablist"
      aria-label={t('intel.subtabs.label')}
      ref={listRef}
      onKeyDown={onKeyDown}
    >
      {tabs.map((name) => (
        <button
          key={name}
          type="button"
          role="tab"
          id={`${prefix}Tab-${name}`}
          className={active === name ? 'intel-subtab on' : 'intel-subtab'}
          aria-selected={active === name}
          // Only the active tab names a panel: an inactive tab's panel is not mounted, and
          // `aria-controls` pointing at an id that is not in the DOM is an invalid reference.
          aria-controls={active === name ? `${prefix}Panel-${name}` : undefined}
          tabIndex={active === name ? 0 : -1}
          onClick={() => setIntelSubTab(name)}
        >
          {t(`intel.${prefix}.tab.${name}`)}
        </button>
      ))}
    </div>
  );
}

export function SubPanel({
  prefix,
  name,
  children,
}: {
  prefix: 'uc1' | 'uc2';
  name: string;
  children: ReactNode;
}) {
  return (
    <div
      id={`${prefix}Panel-${name}`}
      role="tabpanel"
      aria-labelledby={`${prefix}Tab-${name}`}
      className="intel-subpanel"
      // A tabpanel holding no focusable element of its own still has to be reachable by
      // keyboard after the strip, so the reader can scroll it.
      tabIndex={0}
    >
      {children}
    </div>
  );
}

export default SubTabs;
